'use client'

import { BookOpen, ExternalLink, LogOut, Monitor, Moon, Settings, Slack, Sun } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useTheme } from 'next-themes'

import { Avatar, AvatarFallback,AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import kratos from '@/utils/ory-client'
import { userInitials } from '@/utils/user-initials'
import { appendWorkspaceScopeToPath } from '@/utils/workspace-scope'

const DOCS_URL = 'https://docs.fastfold.ai'
const SLACK_URL =
  'https://fastfoldcommunity.slack.com/join/shared_invite/zt-3azbiw7qv-majshu97TymCma_TwFT6mw#/shared-invite/email'
const STATUS_URL = 'https://status.fastfold.ai'

export function NavUserSkeleton() {
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          size="lg"
          className={`justify-center data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground ${
            isCollapsed ? '' : ''
          }`}
          // Keep it inert during auth hydration/loading.
          disabled
        >
          <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : ''}`}>
            <Skeleton className={`${isCollapsed ? 'h-5 w-5' : 'h-6 w-6'} rounded-full`} />
            {!isCollapsed ? <Skeleton className="ml-3 h-4 w-28" /> : null}
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

async function handleSignOut(router: any) {
  try {
    const { data: logoutFlow } = await kratos.createBrowserLogoutFlow(
      undefined,
      {
        withCredentials: true,
        headers: {
          Accept: 'application/json'
        }
      }
    )

    await kratos.updateLogoutFlow(
      {
        token: logoutFlow.logout_token
      },
      {
        withCredentials: true,
        headers: {
          Accept: 'application/json'
        }
      }
    )

    router.push('/login')
  } catch (error) {
    console.error('Logout failed:', error)
    // Still redirect to login on error
    router.push('/login')
  }
}

export function NavUser({
  user
}: {
  user: {
    name: string
    email: string
    avatar?: string
  }
}) {
  const { isMobile, state } = useSidebar()
  const router = useRouter()
  const { theme, setTheme } = useTheme()
  const { activeTeamId } = useWorkspace()

  const openExternal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const themeMode: 'light' | 'dark' | 'system' =
    theme === 'light' || theme === 'dark' || theme === 'system' ? theme : 'system'

  const ThemeButton = ({
    mode,
    label,
    children
  }: {
    mode: 'light' | 'dark' | 'system'
    label: string
    children: React.ReactNode
  }) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setTheme(mode)
      }}
      className={[
        'inline-flex h-7 w-7 items-center justify-center rounded-md border',
        'bg-background hover:bg-muted',
        themeMode === mode ? 'border-foreground/20 bg-muted' : 'border-border'
      ].join(' ')}
    >
      {children}
    </button>
  )

  return (
    <SidebarMenu >
      <SidebarMenuItem >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={`data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground ${
                state === 'collapsed' ? 'justify-center' : ''
              }`}
            >
              <div className={`flex items-center ${state === 'collapsed' ? 'justify-center w-full' : ''}`}>
                <Avatar className={`${state === 'expanded' ? 'h-6 w-6' : 'h-5 w-5'} rounded-full`}>
                  {user.avatar ? (
                    <AvatarImage src={user.avatar} alt={user.name} className="rounded-full" />
                  ) : null}
                  <AvatarFallback className="rounded-full text-[10px] font-medium">
                    {userInitials(user.name, user.email)}
                  </AvatarFallback>
                </Avatar>
                {state === 'expanded' && (
                  <>
                    <div className="grid flex-1 pl-3 text-left text-sm leading-tight">
                      <span className="truncate font-medium leading-none">{user.name}</span>
                    </div>
                  </>
                )}
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-[300px] rounded-xl border bg-background p-1.5 shadow-xl"
            side="top"
            align="start"
            sideOffset={10}
          >
            <DropdownMenuItem
              className="px-3 py-2 cursor-pointer"
              onClick={() => router.push(appendWorkspaceScopeToPath('/settings', activeTeamId))}
            >
              <div className="flex w-full items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold leading-tight">{user.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                </div>
                <Settings className="h-4 w-4 text-muted-foreground" />
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              <DropdownMenuItem
                // Keep menu open while switching theme
                onSelect={(e) => e.preventDefault()}
                className="px-2.5 py-1.5 text-sm flex items-center justify-between"
              >
                <span>Theme</span>
                <span className="flex items-center gap-2">
                  <ThemeButton mode="light" label="Light theme">
                    <Sun className="h-3.5 w-3.5" />
                  </ThemeButton>
                  <ThemeButton mode="system" label="System theme">
                    <Monitor className="h-3.5 w-3.5" />
                  </ThemeButton>
                  <ThemeButton mode="dark" label="Dark theme">
                    <Moon className="h-3.5 w-3.5" />
                  </ThemeButton>
                </span>
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              <DropdownMenuItem
                className="px-2.5 py-1.5 text-sm cursor-pointer flex items-center justify-between"
                onClick={() => openExternal(DOCS_URL)}
              >
                <span>Docs</span>
                <BookOpen className="h-4 w-4 text-muted-foreground" />
              </DropdownMenuItem>

              <DropdownMenuItem
                className="px-2.5 py-1.5 text-sm cursor-pointer flex items-center justify-between"
                onClick={() => openExternal(SLACK_URL)}
              >
                <span>Join Slack</span>
                <Slack className="h-4 w-4 text-muted-foreground" />
              </DropdownMenuItem>

              <DropdownMenuItem
                className="px-2.5 py-1.5 text-sm cursor-pointer flex items-center justify-between"
                onClick={() => openExternal(STATUS_URL)}
              >
                <span>Status</span>
                <ExternalLink className="h-4 w-4 text-muted-foreground" />
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="px-2.5 py-1.5 text-sm cursor-pointer flex items-center justify-between"
              onClick={() => handleSignOut(router)}
            >
              <span>Log out</span>
              <LogOut className="h-4 w-4 text-muted-foreground" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
